import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button, Card, Dropdown, Field, Input, MessageBar, MessageBarBody, Option, Spinner, Subtitle2, Table, TableBody,
  TableCell, TableHeader, TableHeaderCell, TableRow, Text, makeStyles, tokens,
} from '@fluentui/react-components';
import { api } from '../../api/client';
import type { DeploymentSession, GuardianInstallation, ProductManifest } from '../../api/types';

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  form: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: tokens.spacingHorizontalM, alignItems: 'end' },
});

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Where a deployment starts: Guardian's worklist (licences sold but not yet installed, failed
 * installs, installs waiting on the customer's Azure work) — or a manual start for a tenant
 * that already holds a licence. Guardian decides entitlement either way; nothing here can
 * create a licence.
 */
export function StartPanel() {
  const styles = useStyles();
  const navigate = useNavigate();
  const products = useQuery({ queryKey: ['products'], queryFn: () => api<ProductManifest[]>('products') });
  const worklist = useQuery({ queryKey: ['worklist'], queryFn: () => api<GuardianInstallation[]>('worklist') });

  const [productId, setProductId] = useState('');
  const [tenantId, setTenantId] = useState('');

  const start = useMutation({
    mutationFn: (body: { tenantId: string; productId: string }) => api<DeploymentSession>('sessions', { body }),
    onSuccess: (s) => navigate(`/deploy/${s.id}`),
  });

  const productFor = (appId: string) => products.data?.find((p) => p.guardianAppId === appId);

  return (
    <div className={styles.stack}>
      <Card>
        <Subtitle2>Ready to deploy (from FirstIQ Guardian)</Subtitle2>
        {worklist.isLoading && <Spinner size="small" label="Reading the worklist…" />}
        {worklist.error && <MessageBar intent="error"><MessageBarBody>{(worklist.error as Error).message}</MessageBarBody></MessageBar>}
        {worklist.data?.length === 0 && <Text>Nothing waiting. New sales appear here once Guardian issues the licence.</Text>}
        {!!worklist.data?.length && (
          <Table size="small">
            <TableHeader><TableRow>
              <TableHeaderCell>Customer</TableHeaderCell><TableHeaderCell>Product</TableHeaderCell>
              <TableHeaderCell>State</TableHeaderCell><TableHeaderCell>Since</TableHeaderCell><TableHeaderCell />
            </TableRow></TableHeader>
            <TableBody>
              {worklist.data.map((w) => {
                const p = productFor(w.appId);
                return (
                  <TableRow key={`${w.tenantId}-${w.appId}`}>
                    <TableCell>{w.CustomerName || w.tenantId}</TableCell>
                    <TableCell>{p?.displayName ?? w.appId}</TableCell>
                    <TableCell>{w.State}{w.LastError ? ` — ${w.LastError}` : ''}</TableCell>
                    <TableCell>{new Date(w.StateChangedAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <Button appearance="primary" disabled={!p || start.isPending}
                              title={p ? undefined : 'No deployable manifest for this product yet'}
                              onClick={() => p && start.mutate({ tenantId: w.tenantId, productId: p.productId })}>
                        Deploy
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card>
        <Subtitle2>Start a deployment</Subtitle2>
        <div className={styles.form}>
          <Field label="Product" required>
            <Dropdown placeholder={products.isLoading ? 'Loading…' : 'Choose a product'}
                      value={products.data?.find((p) => p.productId === productId)?.displayName ?? ''}
                      onOptionSelect={(_, d) => setProductId(d.optionValue ?? '')}>
              {products.data?.map((p) => (
                <Option key={p.productId} value={p.productId} disabled={!p.packageUploaded}
                        text={p.displayName}>
                  {`${p.displayName} ${p.version}${p.packageUploaded ? '' : ' (package not uploaded)'}`}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Field label="Customer tenant ID" required
                 validationMessage={tenantId && !GUID.test(tenantId.trim()) ? 'Enter the Entra tenant GUID' : undefined}>
            <Input value={tenantId} onChange={(_, d) => setTenantId(d.value)} placeholder="00000000-0000-0000-0000-000000000000" />
          </Field>
          <Button appearance="primary" disabled={!productId || !GUID.test(tenantId.trim()) || start.isPending}
                  onClick={() => start.mutate({ tenantId: tenantId.trim(), productId })}>
            {start.isPending ? 'Starting…' : 'Start'}
          </Button>
        </div>
        {start.error && <MessageBar intent="error"><MessageBarBody>{(start.error as Error).message}</MessageBarBody></MessageBar>}
      </Card>
    </div>
  );
}
